import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Trash2, Send, Copy, CheckCircle2, Users as UsersIcon, Link as LinkIcon, ShieldCheck } from "lucide-react";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) { toast.error("Erro ao carregar usuários"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "" }); setOpen(true); };

  const save = async () => {
    try {
      const r = await api.post("/users", form);
      setUsers((p) => [r.data, ...p]);
      setInviteInfo({ name: r.data.name, email: r.data.email, invite_url: r.data.invite_url });
      setInviteCopied(false);
      setOpen(false);
      toast.success("Administrador criado. Convite gerado.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar"); }
  };

  const resend = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/resend-invite`);
      setInviteInfo({ name: u.name, email: u.email, invite_url: r.data.invite_url });
      setInviteCopied(false);
      toast.success("Convite reenviado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleActive = async (u) => {
    try {
      const r = await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? r.data : x)));
      toast.success(r.data.is_active ? "Usuário ativado" : "Usuário desativado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remover o administrador ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((p) => p.filter((x) => x.id !== u.id));
      toast.success("Usuário removido");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const copyInvite = async () => {
    if (!inviteInfo?.invite_url) return;
    try {
      await navigator.clipboard.writeText(inviteInfo.invite_url);
      setInviteCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setInviteCopied(false), 2500);
    } catch (_e) { void _e; toast.error("Falha ao copiar"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Equipe BAUEN</p>
          <h1 className="font-heading text-3xl font-bold mt-1">Administradores</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gerencie quem pode acessar o portal com perfil de administrador.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-user-btn">
          <Plus size={16} /> Novo administrador
        </Button>
      </header>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <UsersIcon className="mx-auto mb-3 text-muted-foreground/50" />
              Nenhum administrador cadastrado.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isSelf = u.id === me?.id;
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <ShieldCheck size={14} className="text-primary" />
                          {u.name}
                          {isSelf && <span className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted px-1.5 py-0.5 rounded">você</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell>
                        <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${u.is_active ? "text-emerald-600 border-emerald-500/30 bg-emerald-500/10" : "text-muted-foreground border-border bg-muted"}`}>
                          {u.is_active ? "ativo" : "inativo"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={isSelf} data-testid={`toggle-user-${u.id}`} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => resend(u)} title="Reenviar convite" data-testid={`resend-user-${u.id}`}>
                          <Send size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(u)} disabled={isSelf}
                          className="text-brand-red hover:text-brand-red disabled:text-muted-foreground disabled:cursor-not-allowed" data-testid={`del-user-${u.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo administrador</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nome completo *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" placeholder="Ex: Carla Mendes" />
            </div>
            <div>
              <Label className="text-xs">E-mail *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" placeholder="carla@bauen.com.br" />
            </div>
            <p className="text-xs text-muted-foreground">
              O sistema enviará um convite por e-mail para que o novo administrador crie sua própria senha de acesso.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-form-cancel">Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.email} data-testid="user-form-save">Criar e enviar convite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LinkIcon size={18} className="text-primary" /> Convite de administrador gerado</DialogTitle>
          </DialogHeader>
          {inviteInfo && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enviamos um e-mail para <b className="text-foreground">{inviteInfo.email}</b> com o link para
                <b className="text-foreground"> {inviteInfo.name}</b> criar sua senha de acesso.
              </p>
              <div className="rounded-lg border bg-muted/40 p-3">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Link de convite (7 dias)</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs break-all font-mono" data-testid="user-invite-url">{inviteInfo.invite_url}</code>
                  <Button size="sm" variant="outline" onClick={copyInvite} className="shrink-0 gap-1.5" data-testid="copy-user-invite-btn">
                    {inviteCopied ? <><CheckCircle2 size={14} className="text-emerald-500" /> Copiado</> : <><Copy size={14} /> Copiar</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInviteInfo(null)}>Concluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
